import { useTranslation } from 'next-i18next';
import styles from './style.module.scss';
import Button, { TERTIARY_THEME } from 'components/control/button';
import NotificationIcon from 'components/icon/notification-icon';
import { searchFilterIds } from 'constants/test-constants';
import { LOCALE_SEARCH_PATHS, Namespaces } from 'types/locales';
import CloseIcon from 'components/icon/close-icon';
import { PartialDeep } from 'type-fest';
import { FilterItemData, ListingFilterBarType } from './listing_filter_bar';
import { useCallback } from 'react';
import { buildClassName } from 'utils/build-class-name';
import { createFilterConfigs } from './filter-chip-configs';
interface FilterItemProps {
  filter: FilterItemData;
  onValueChange: (filters: PartialDeep<ListingFilterBarType>, changedFilter?: string) => void;
  removeFilter: (filterType: FilterItemData['type']) => void;
}

const FilterItem: React.FC<FilterItemProps & { filterConfigs: ReturnType<typeof createFilterConfigs> }> = ({ filter, onValueChange, removeFilter, filterConfigs }) => {
  const config = filterConfigs[filter.type];

  if (!config) {
    return null;
  }

  return (
    <div className={styles['active-filter-item']}>
      <span className={styles['filter-value']}>{config.render(filter)}</span>
      <CloseIcon
        onClick={() => {
          config.removeAction(onValueChange);
          removeFilter(filter.type);
        }}
        className={styles['remove-filter-button']}
      />
    </div>
  );
};

interface FilterChipBarProps {
  removeFilterItem?: (filterType: FilterItemData['type']) => void;
  onValueChange: (filters: PartialDeep<ListingFilterBarType>, changedFilter?: string) => void;
  onSaveSearchButtonClick?: () => void;
  activeFilterItems: FilterItemData[];
  setActiveFilterItems: (activeFilterItems: FilterItemData[]) => void;
  filters: ListingFilterBarType;
  isMobile?: boolean;
  oneLine?: boolean;
}

export const FilterChipBar = ({ onValueChange, onSaveSearchButtonClick, activeFilterItems, setActiveFilterItems, isMobile, oneLine = true }: FilterChipBarProps) => {
  const { t } = useTranslation(Namespaces.SEARCH);
  const filterConfigs = createFilterConfigs(t);

  const removeFilterItem = useCallback((filterType: FilterItemData['type']) => {
    const newFilterItems = activeFilterItems.filter(item => item.type !== filterType);
    setActiveFilterItems(newFilterItems);
  }, [activeFilterItems, setActiveFilterItems]);
  
  return (
    <div className={buildClassName(styles['active-filters-display'], !oneLine && styles['multi-line'])}>
      <>
        <div className={styles['active-filter-items-wrapper']}>
          {activeFilterItems.map((filter, index) => (
            <FilterItem
              key={`${filter.type}-${index}`}
              filter={filter}
              onValueChange={onValueChange}
              removeFilter={removeFilterItem}
              filterConfigs={filterConfigs}
            />
          ))}
        </div>
        {!isMobile && onSaveSearchButtonClick &&
          <Button
            theme={TERTIARY_THEME}
            label={t(LOCALE_SEARCH_PATHS.SAVED_SEARCH_BUTTON_SAVE, {
              defaultValue: 'Save Search',
            })}
            Icon={NotificationIcon}
            testId={searchFilterIds.saveSearchButtonMapPage}
            onClick={onSaveSearchButtonClick}
          />
        }
      </>
    </div>
  );
};